// Mapping type -> nom de modale
export const MODAL_BY_TYPE = {
  identity: 'ad_file_accept_identity',
  selfie: 'ad_file_accept_simple',
  visa: 'ad_file_accept_visa',
  card_vitality: 'ad_file_accept_card_vitality',
  proof_of_address: 'ad_file_accept_simple',
  accommodation_of_address: 'ad_file_accept_simple',
  hostIdentity: 'ad_file_accept_simple',
  // Lets the refuse modal go back to the proof; not in DOCUMENT_PRIORITY on purpose,
  // a proof is opened by its id, never as the next document of a type.
  visa_complementary: 'ad_file_visa_complementary',
  // Same for a licence or CACES entry, opened by its id from the licence list.
  licence: 'ad_file_accept_licence',
};

// Ordre de priorité pour la vérification des documents
const DOCUMENT_PRIORITY = [
  'identity',
  'selfie',
  'visa',
  'card_vitality',
  'proof_of_address',
  'accommodation_of_address',
  'hostIdentity',
];

/**
 * Récupère le statut de validation d'un type de document
 * @param {object} tempWorker - L'objet tempWorker
 * @param {string} docType - Le type de document
 * @returns {string|null} Le statut de validation ('1' = à vérifier, '2' = validé, '3' = refusé)
 */
export const getDocumentValidationStatus = (tempWorker, docType) => {
  if (!tempWorker) return null;
  return tempWorker[docType]?.validated;
};

/**
 * Trouve le prochain document non vérifié (statut '1' = à vérifier)
 * @param {object} tempWorker - L'objet tempWorker
 * @param {Array} allDocuments - Liste de tous les documents disponibles
 * @param {string} currentType - Type du document actuel (à exclure)
 * @returns {object|null} { type, modalName } ou null si aucun document à vérifier
 */
export const findNextUnverifiedDocument = (tempWorker, allDocuments, currentType) => {
  if (!tempWorker || !allDocuments) return null;

  // Filtrer les documents disponibles (qui ont des fichiers)
  const availableTypes = allDocuments.map(doc => doc.type);

  // Chercher dans l'ordre de priorité
  for (const docType of DOCUMENT_PRIORITY) {
    // Ignorer le document actuel
    if (docType === currentType) continue;
    
    // Vérifier si ce type de document existe dans les documents disponibles
    if (!availableTypes.includes(docType)) continue;
    
    // Vérifier le statut de validation
    const status = getDocumentValidationStatus(tempWorker, docType);
    
    // '1' = à vérifier (pas encore validé ni refusé)
    if (status === '1') {
      return {
        type: docType,
        modalName: MODAL_BY_TYPE[docType],
      };
    }
  }

  return null;
};

/**
 * Ouvre le prochain document non vérifié ou ferme le modal si tout est vérifié
 * @param {object} params
 * @param {object} params.tempWorker - L'objet tempWorker
 * @param {Array} params.allDocuments - Liste de tous les documents
 * @param {string} params.currentType - Type du document actuel
 * @param {Function} params.setAdminType - Setter pour le type de document
 * @param {Function} params.switchModal - Fonction pour changer de modal sans fermer
 * @param {Function} params.toggle - Fonction pour fermer le modal actuel
 * @returns {boolean} true si un nouveau document a été ouvert, false sinon
 */
export const openNextUnverifiedDocument = ({
  tempWorker,
  allDocuments,
  currentType,
  setAdminType,
  switchModal,
  toggle,
}) => {
  const nextDoc = findNextUnverifiedDocument(tempWorker, allDocuments, currentType);
  
  if (nextDoc) {
    setAdminType(nextDoc.type);
    switchModal(nextDoc.modalName);
    return true;
  }
  
  // Aucun document restant à vérifier, fermer le modal
  toggle();
  return false;
};
